/**
 * Ant Design theme configuration.
 * These tokens are the JS counterpart of the SCSS/CSS variables in
 * src/styles/. Keep the two in sync so AntD components and custom
 * SCSS share one visual language.
 */
import { theme as antdTheme } from 'antd'
import type { ThemeConfig } from 'antd'

export type ThemeMode = 'light' | 'dark'

/**
 * Brand palette (AMLiQ teal), mirrored from Figma and kept in sync with the
 * Tailwind `@theme` tokens in src/styles/tailwind.css.
 */
const BRAND = {
  primary: '#1999b9',
  primaryHover: '#157f9b',
  primaryActive: '#126a82',
  canvas: '#f8f6f3', // app content background
} as const

const sharedToken: ThemeConfig['token'] = {
  colorPrimary: BRAND.primary,
  colorSuccess: '#52c41a',
  colorWarning: '#faad14',
  colorError: '#ff4d4f',
  colorLink: '#1a99b9',
  colorLinkHover: '#1a99b9d9',
  colorLinkActive: '#126a82',
  borderRadius: 8,
  fontSize: 14,
  fontFamily:
    "'Neulis Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  wireframe: false,
}

export const lightTheme: ThemeConfig = {
  algorithm: antdTheme.defaultAlgorithm,
  token: sharedToken,
  components: {
    Layout: {
      headerBg: '#ffffff',
      siderBg: '#ffffff',
      bodyBg: BRAND.canvas,
    },
    Menu: {
      // Match the Figma sidebar menu metrics.
      itemBorderRadius: 8,
      itemHeight: 40,
      itemMarginInline: 8,
      iconMarginInlineEnd: 10,
      itemSelectedColor: BRAND.primary,
      itemSelectedBg: '#e6f4f8',
    },
    Button: {
      colorPrimaryHover: BRAND.primaryHover,
      colorPrimaryActive: BRAND.primaryActive,
    },
    Table: {
      headerBg: '#f0efed',
      headerColor: 'rgba(0,0,0,0.88)',
      borderColor: '#f0f0f0',
      cellPaddingBlock: 12,
      cellPaddingInline: 12,
      rowHoverBg: '#faf9f7',
    },
    Tabs: {
      // Measured off the Verifications v2 frame (Figma `1590-25350`): 13px
      // medium labels, warm grey when idle. The selected label and the ink bar
      // are left at their defaults, which already resolve to `colorPrimary`
      // (#1999b9) — the value the frame uses.
      fontSize: 13,
      itemColor: '#766e63',
    },
  },
}

export const darkTheme: ThemeConfig = {
  algorithm: antdTheme.darkAlgorithm,
  token: sharedToken,
  components: {
    Layout: {
      headerBg: '#141414',
      siderBg: '#141414',
      bodyBg: '#000000',
    },
    Menu: {
      itemBorderRadius: 8,
      itemHeight: 40,
    },
  },
}

export function getAntdTheme(mode: ThemeMode): ThemeConfig {
  return mode === 'dark' ? darkTheme : lightTheme
}
